import { Box, Divider, Grid, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AxiosResponse } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../../theme/ui/Atoms/Button';
import { fromBase64 } from '../../../../shared/util/base64';
import Input from '../../../../theme/ui/Atoms/Input';
import InputSelect from '../../../../theme/ui/Atoms/InputSelect';
import useBack from '../../../../shared/util/route-dom/hooks/useBack';
import BackButton from '../BackButton';
import ContainerBox from '../ContainerBox';
import {
  CheckIcon,
  ErrorOutlineIcon,
  ScanAirConnectIcon,
} from '../../../../assets';
import useErrorMessage from '../../../../shared/hooks/useErrorMessage';
import AlertMessage from '../AlertMessage';
import { CommanderAPI } from '../../../../service/device/CommanderAPI/CommanderAPI';
import LocationAPI from '../../../../service/locations/LocationAPI';
import useMachineTypes from '../../../../redux/workspace/hooks/useMachineTypes';
import CommanderAirConnectAPI from '../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI';
import FlexeserveDeviceAPI from '../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import useMyWorkspace from '../../../../redux/workspace/hooks/useMyWorkspace';
import { useDeviceNameOptions } from '../../../../redux/workspace/util';

const validationSchema = yup.object({
  cloudConnectId: yup.string().required('initial_setup.cloud_id_required'),
  unitName: yup.string().required('initial_setup.unit_name_required'),
  machineTypeId: yup.string().required('initial_setup.model_required'),
  serialNumber: yup.string().nullable(),
});

function FormCommanderSetUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showError = useErrorMessage();
  const onClickBack = useBack('/');

  const { storeId = '' } = useParams<{
    storeId: string;
  }>();

  const [triggerGetCommander, resultCommander] =
    CommanderAPI.useLazyGetDeviceByCloudConnectIdQuery();

  const {
    error: errorCommander,
    data: dataCommander,
    isFetching: isFetchingCommander,
    isSuccess: isSuccessCommander,
  } = resultCommander;

  const errorCommanderNotFound =
    isSuccessCommander && !dataCommander
      ? new Error('initial_setup.connect_commander.commander_not_found_error')
      : undefined;

  const errorMitigated =
    // eslint-disable-next-line eqeqeq
    (errorCommander as AxiosResponse)?.status == 404
      ? new Error('initial_setup.connect_commander.commander_not_found_error')
      : errorCommander;

  const { data: machineTypes, isLoading: isLoadingMachineTypes } =
    useMachineTypes();

  const { data: myWorkspace } = useMyWorkspace();

  const [
    registerCommanderByCloudConnectId,
    { isLoading: isLoadingRegisteringCommander },
  ] = LocationAPI.endpoints.registerCommanderByCloudConnectId.useMutation();

  const [
    registerComposableDevice,
    { isLoading: isRegisteringComposableDevice },
  ] =
    CommanderAirConnectAPI.endpoints.registerNewComposableDevice.useMutation();

  const [triggerGetStoreDevices] =
    FlexeserveDeviceAPI.useLazyGetStoreDevicesQuery();

  const machineTypeOptions = (machineTypes ?? [])
    .filter((machineType) => machineType.labels?.nodeType !== '2')
    .map((machineType) => ({
      value: machineType.id,
      text: machineType.name,
    }));

  const unitNameValues = useDeviceNameOptions();

  const unitNameOptions = unitNameValues.map((name) => ({
    value: name,
    text: name,
  }));

  const formik = useFormik({
    initialValues: {
      cloudConnectId: '',
      unitName: '',
      machineTypeId: '',
      serialNumber: '',
    },
    validationSchema,
    onSubmit: () => {
      // submission handled by onClickNext
    },
  });

  const catchEnter = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      triggerGetCommander(formik.values.cloudConnectId);
    }
  };

  useEffect(() => {
    if (dataCommander?.serialNumber) {
      const prefix = dataCommander.articalNumber
        ? `${dataCommander.articalNumber}-`
        : '';
      formik.setFieldValue(
        'serialNumber',
        `${prefix}${dataCommander.serialNumber}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCommander]);

  const onClickSearch = () => {
    if (formik.values.cloudConnectId) {
      triggerGetCommander(formik.values.cloudConnectId);
    }
  };

  const onClickNext = () => {
    if (!dataCommander) return;

    const commanderId = fromBase64(dataCommander.uuid ?? '');
    const serialNumber = formik.values.serialNumber;
    // Generate addresses from 1 to 5
    // const addresses = Array.from({ length: 5 }, (_, i) => (i + 1).toString());

    registerCommanderByCloudConnectId({
      storeId,
      cloudConnectId: formik.values.cloudConnectId,
      data: {
        unitId: crypto.randomUUID(),
        unitName: formik.values.unitName,
        serialnumber: serialNumber ? serialNumber : null,
        model:
          machineTypes?.find((mt) => mt.id === formik.values.machineTypeId)
            ?.name ?? formik.values.machineTypeId,
      },
    })
      .unwrap()
      // TODO maybe try to install devices
      .then(() => triggerGetStoreDevices(storeId).unwrap())
      .then((storeDevices) => {
        const device = storeDevices?.find((d) => d.commanderId === commanderId);
        const shelfMappings = (device?.shelfMappings ?? []).filter(
          (m): m is NonNullable<typeof m> => m !== null
        );
        const controlUnits = shelfMappings.map((mapping) => ({
          deviceId: commanderId,
          controlUnitId: mapping.cuid?.toString(),
          controlUnitBusAddress: mapping.cuaddr,
        }));
        const zoneMapping = shelfMappings.reduce(
          (acc, mapping, index) => ({
            ...acc,
            [index + 1]: mapping.cuaddr,
          }),
          {} as Record<string, string>
        );

        return registerComposableDevice({
          workspaceId: myWorkspace?.id ?? '',
          storeId,
          data: {
            // commanderSerialNum: serialNumber,
            commanderId: commanderId,
            storeId,
            devices: [
              {
                mac: '',
                name: formik.values.unitName,
                serialnumber: null,
                model:
                  machineTypes?.find(
                    (mt) => mt.id === formik.values.machineTypeId
                  )?.name ?? formik.values.machineTypeId,
                composableDevice: {
                  machineTypeId: formik.values.machineTypeId,
                  controlUnits,
                  labels: {},
                },
                zoneMapping,
              },
            ],
          },
        }).unwrap();
      })
      .then(() => {
        navigate(
          `../commander/${commanderId}/serialNumber/${
            serialNumber || 'unknown'
          }/store/${storeId}/actions/devices-overview-manager`
        );
      })
      .catch((error) => showError(error));
  };

  const error = errorMitigated || errorCommanderNotFound;
  const isLoading =
    isLoadingRegisteringCommander || isRegisteringComposableDevice;

  const getEndAdornment = () => {
    if (error) {
      return (
        <Box color={'error.main'} fontSize={'1.4rem'} display={'flex'}>
          <ErrorOutlineIcon />
        </Box>
      );
    }
    if (dataCommander) {
      return (
        <Box color={'success.main'} fontSize={'1.4rem'} display={'flex'}>
          <CheckIcon />
        </Box>
      );
    }

    return undefined;
  };

  return (
    <ContainerBox maxWidth="sm">
      <Typography variant="h1">
        {t('initial_setup.connect_commander.title')}
      </Typography>
      <Grid container spacing={2} my={4}>
        <Grid size={12}>
          <Typography variant="body2">
            {t('initial_setup.cloud_id_label')} *
          </Typography>
        </Grid>
        <Grid size="grow">
          <Input
            placeholder={t('initial_setup.cloud_id_placeholder') ?? undefined}
            fullWidth
            required
            aria-required
            name="cloudConnectId"
            value={formik.values.cloudConnectId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            onKeyDown={catchEnter}
            error={
              formik.touched.cloudConnectId &&
              Boolean(formik.errors.cloudConnectId)
            }
            helperText={
              formik.touched.cloudConnectId && formik.errors.cloudConnectId
                ? t(formik.errors.cloudConnectId)
                : ''
            }
            endAdornment={getEndAdornment()}
          />
        </Grid>
        <Grid width={'150px'}>
          <Button
            variant="contained"
            color="primary"
            rounded
            fullWidth
            disabled={!formik.values.cloudConnectId}
            onClick={onClickSearch}
            loading={isFetchingCommander}
          >
            <ScanAirConnectIcon height={'2em'} />
          </Button>
        </Grid>
      </Grid>

      {dataCommander && (
        <Grid container spacing={2} my={2}>
          <Grid size={12}>
            <InputSelect
              select
              id="unitName"
              name="unitName"
              label={t('initial_setup.unit_name_label')}
              placeholder={
                t('initial_setup.unit_name_placeholder') ?? undefined
              }
              fullWidth
              required
              aria-required
              value={formik.values.unitName}
              onChange={(v: string) => formik.setFieldValue('unitName', v)}
              onBlur={formik.handleBlur}
              error={formik.touched.unitName && Boolean(formik.errors.unitName)}
              helperText={
                formik.touched.unitName && formik.errors.unitName
                  ? t(formik.errors.unitName)
                  : ''
              }
              options={unitNameOptions}
            />
          </Grid>
          <Grid size={12}>
            <InputSelect
              select
              id="machineTypeId"
              name="machineTypeId"
              label={t('initial_setup.model_label')}
              placeholder={t('initial_setup.model_placeholder') ?? undefined}
              fullWidth
              required
              aria-required
              value={formik.values.machineTypeId}
              onChange={(v: string) => formik.setFieldValue('machineTypeId', v)}
              onBlur={formik.handleBlur}
              error={
                formik.touched.machineTypeId &&
                Boolean(formik.errors.machineTypeId)
              }
              helperText={
                formik.touched.machineTypeId && formik.errors.machineTypeId
                  ? t(formik.errors.machineTypeId)
                  : ''
              }
              options={machineTypeOptions}
              disable={isLoadingMachineTypes}
            />
          </Grid>
          <Grid size={12}>
            <Input
              label={t('initial_setup.serial_number_label')}
              placeholder={
                t('initial_setup.serial_number_placeholder') ?? undefined
              }
              fullWidth
              name="serialNumber"
              value={formik.values.serialNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
      )}

      <Divider />
      <Box mt={2} minHeight={'100px'} padding={4}>
        {!!error && <AlertMessage error={error} />}
      </Box>
      <Grid container spacing={2} justifyContent={'space-between'}>
        <Grid
          size={{
            xs: 12,
            sm: 2,
          }}
        >
          <BackButton onClick={onClickBack} />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            rounded
            fullWidth
            loading={isLoading}
            disabled={
              !!errorCommander ||
              !dataCommander ||
              !formik.values.unitName ||
              !formik.values.machineTypeId
            }
            onClick={onClickNext}
          >
            {t('button.next')}
          </Button>
        </Grid>
      </Grid>
    </ContainerBox>
  );
}

export default FormCommanderSetUp;
